import { useApiConfig, postJson, ApiConfig } from '../../../composables/useApi';

export interface FeatureSearchResult {
  id: number;
  name: string;
}

export interface FeatureValue {
  id: number;
  value: string;
  position: number;
}

export interface FeatureValueSearchResult {
  id: number;
  value: string;
  feature_id: number;
  feature_name: string;
  label: string;
}

export interface FeatureWithValues {
  feature_id: number;
  feature_name: string;
  values: {
    id: number;
    label: string;
  }[];
}

export interface SelectedFeatureValue {
  id: number;
  label: string;
  feature_id: number;
  feature_name: string;
}

export const featureApi = {
  /**
   * Search features by name
   */
  async searchFeaturesWithConfig(config: ApiConfig, query: string): Promise<FeatureSearchResult[]> {
    const url = `${config.apiFeaturesSearch}?action=searchFeatures`;
    const result = await postJson<{ success: boolean; data: FeatureSearchResult[]; error?: string }>(
      url,
      { q: query, limit: 20 }
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to search features');
    }
    
    return result.data;
  },

  async searchFeatures(query: string): Promise<FeatureSearchResult[]> {
    const api = useApiConfig();
    return featureApi.searchFeaturesWithConfig(api, query);
  },

  /**
   * Get feature values for a specific feature
   */
  async getFeatureValuesWithConfig(config: ApiConfig, featureId: number): Promise<FeatureValue[]> {
    const url = `${config.apiFeaturesSearch}?action=getFeatureValues`;
    const result = await postJson<{ success: boolean; data: FeatureValue[]; error?: string }>(
      url,
      { feature_id: featureId }
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get feature values');
    }
    
    return result.data;
  },

  async getFeatureValues(featureId: number): Promise<FeatureValue[]> {
    const api = useApiConfig();
    return featureApi.getFeatureValuesWithConfig(api, featureId);
  },

  /**
   * Search feature values across all features
   */
  async searchFeatureValuesWithConfig(config: ApiConfig, query: string): Promise<FeatureValueSearchResult[]> {
    const url = `${config.apiFeaturesSearch}?action=searchFeatureValues`;
    const result = await postJson<{ success: boolean; data: FeatureValueSearchResult[]; error?: string }>(
      url,
      { q: query, limit: 20 }
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to search feature values');
    }
    
    return result.data;
  },

  async searchFeatureValues(query: string): Promise<FeatureValueSearchResult[]> {
    const api = useApiConfig();
    return featureApi.searchFeatureValuesWithConfig(api, query);
  },

  /**
   * Get all available feature values grouped by feature
   */
  async getAllFeatureValuesWithConfig(config: ApiConfig): Promise<FeatureWithValues[]> {
    const url = `${config.apiFeaturesSearch}?action=getAllFeatureValues`;
    const result = await postJson<{ success: boolean; data: FeatureWithValues[]; error?: string }>(
      url,
      {}
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get all feature values');
    }
    
    return result.data;
  },

  async getAllFeatureValues(): Promise<FeatureWithValues[]> {
    const api = useApiConfig();
    return featureApi.getAllFeatureValuesWithConfig(api);
  },

  /**
   * Get specific feature values by their IDs
   */
  async getFeatureValuesByIdsWithConfig(config: ApiConfig, valueIds: number[]): Promise<SelectedFeatureValue[]> {
    if (valueIds.length === 0) {
      return [];
    }

    const url = `${config.apiFeaturesSearch}?action=getFeatureValuesByIds`;
    const result = await postJson<{ success: boolean; data: SelectedFeatureValue[]; error?: string }>(
      url,
      { value_ids: valueIds }
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get feature values');
    }
    
    return result.data;
  },

  async getFeatureValuesByIds(valueIds: number[]): Promise<SelectedFeatureValue[]> {
    const api = useApiConfig();
    return featureApi.getFeatureValuesByIdsWithConfig(api, valueIds);
  },
};
